import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faSpinner, faAnchor, faIdBadge, faTag, faPlus,
  faRoute, faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import VesselSearchAutocomplete from '../VesselSearchAutocomplete';
import PortSearchAutocomplete from '../PortSearchAutocomplete';
import { globeService } from '../../services/globeService';
import { useFlashMessage } from '../FlashMessage';
import { getFlagUrl } from '../../utils/flagUtils';
import './VesselWatchlistPanel.css';

// Keep just the human "Name" segment of a stored canonical port string
// ("Name, Country, UNLOCODE") for compact display; '' stays ''.
const portName = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  return s.includes(',') ? s.split(',')[0].trim() : s;
};

/**
 * Per-user vessel watchlist for the Shipment Overview dashboard.
 *
 * Visual language matches the Deliveries section -- same divider, card border,
 * hover lift and stacked-metric layout. A single accent color (teal) is used
 * so watchlist cards stay visually distinct from delivery cards (which rotate
 * through the CARD_ACCENTS palette).
 *
 * Hover-link: hovering any card reports a vessel key upward; the parent
 * uses the same key to glow the matching map marker + port dots. Clicking
 * a card flies the map to that vessel.
 */
const VesselWatchlistPanel = ({
  watchlist = [],
  hoveredVesselKey = null,
  onHoverVessel = null,
  onCardClick = null,
  onAdded,
  onRemoved,
  onPortsUpdated,
}) => {
  const { showError } = useFlashMessage();
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  // Which card's load/discharge port editor is open (one at a time), and which
  // entry is mid-save so its editor can show a spinner.
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  // When a port editor opens, pull it toward the top of the scrollable panel so
  // its downward-opening discharge dropdown isn't clipped by the panel's bottom
  // edge (paired with a scroll spacer rendered below the grid).
  const editorRef = useRef(null);
  useEffect(() => {
    if (editingId != null && editorRef.current) {
      editorRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [editingId]);

  // Persist a picked port (or a cleared '') onto the watchlist entry, then let
  // the parent re-resolve its dots + route line on the map. Ports are stored in
  // the same canonical "Name, Country, UNLOCODE" string deliveries use.
  const savePort = useCallback(async (entry, field, canonical) => {
    setSavingId(entry.id);
    try {
      const updated = await globeService.updateWatchlistVessel(entry.id, { [field]: canonical });
      if (onPortsUpdated) onPortsUpdated(updated);
    } catch (err) {
      showError(err.message || 'Could not update port');
    } finally {
      setSavingId(null);
    }
  }, [onPortsUpdated, showError]);

  const handlePortSelect = useCallback((entry, field, port) => {
    const canonical = port
      ? [port.port_name, port.country, port.port_code].filter(Boolean).join(', ')
      : '';
    return savePort(entry, field, canonical);
  }, [savePort]);

  const handleSelect = useCallback(async (vessel) => {
    if (!vessel) return;
    if (!vessel.imo && !vessel.mmsi) {
      showError('Selected vessel has no IMO or MMSI -- cannot track.');
      return;
    }

    setAdding(true);
    try {
      const { entry, isDuplicate } = await globeService.addWatchlistVessel({
        imo: vessel.imo || '',
        mmsi: vessel.mmsi || '',
        vessel_name: vessel.ship_name || vessel.vessel_name || '',
        ship_type: vessel.ship_type || '',
        flag: vessel.flag || '',
      });
      if (onAdded) onAdded(entry, { isDuplicate });
    } catch (err) {
      showError(err.message || 'Could not add vessel to watchlist');
    } finally {
      setAdding(false);
    }
  }, [onAdded, showError]);

  const handleRemove = useCallback(async (entry) => {
    if (!entry || !entry.id) return;
    setRemovingId(entry.id);
    try {
      await globeService.removeWatchlistVessel(entry.id);
      if (onRemoved) onRemoved(entry.id, { vesselName: entry.vessel_name });
    } catch (err) {
      showError(err.message || 'Could not remove vessel');
    } finally {
      setRemovingId(null);
    }
  }, [onRemoved, showError]);

  return (
    <>
      <div className="so-divider">
        <div className="so-divider-line" />
        <span className="so-divider-label">
          Watchlist {watchlist.length > 0 && <span className="so-divider-count">· {watchlist.length}</span>}
        </span>
        <div className="so-divider-line" />
      </div>

      <div className="vw-search-bar">
        <div className="vw-search-bar-input">
          <VesselSearchAutocomplete
            onSelect={handleSelect}
            placeholder="Search by Name, IMO, MMSI or Call Sign to add a vessel..."
          />
        </div>
        {adding && (
          <span className="vw-search-bar-status">
            <FontAwesomeIcon icon={faSpinner} spin /> Adding...
          </span>
        )}
      </div>

      {watchlist.length === 0 ? (
        <div className="vw-cards-grid">
          <div className="vw-empty-card">
            <FontAwesomeIcon icon={faPlus} className="vw-empty-icon" />
            <div className="vw-empty-content">
              <span className="vw-empty-title">Track any vessel</span>
              <span className="vw-empty-text">
                Use the search above to add vessels you want to monitor.
                They will appear on the map alongside your delivery vessels.
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="vw-cards-grid">
          {watchlist.map((entry) => {
            const cardKey = `w:${entry.id}`;
            const isHighlighted = hoveredVesselKey === cardKey;
            return (
            <div
              key={entry.id}
              className={`vw-card ${isHighlighted ? 'vw-card-highlighted' : ''}`}
              onMouseEnter={() => onHoverVessel && onHoverVessel(cardKey)}
              onMouseLeave={() => onHoverVessel && onHoverVessel(null)}
            >
              <div className="vw-card-accent" />
              <div
                className="vw-card-face"
                onClick={() => onCardClick && onCardClick(entry)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCardClick && onCardClick(entry);
                  }
                }}
              >
                <div className="vw-card-title-row">
                  <span className="vw-card-status">Watching</span>
                  <h4 className="vw-card-name">{entry.vessel_name || 'Unknown vessel'}</h4>
                  {entry.flag && getFlagUrl(entry.flag) ? (
                    <img
                      src={getFlagUrl(entry.flag)}
                      alt={entry.flag}
                      className="vw-card-flag"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="vw-card-id">{entry.flag || '--'}</span>
                  )}
                </div>

                <div className="vw-card-metrics">
                  <div className="vw-card-metric">
                    <FontAwesomeIcon icon={faIdBadge} className="vw-card-metric-icon" />
                    <div className="vw-card-metric-content">
                      <span className="vw-card-metric-label">IMO</span>
                      <span className="vw-card-metric-value">{entry.imo || '--'}</span>
                    </div>
                  </div>
                  <div className="vw-card-metric">
                    <FontAwesomeIcon icon={faAnchor} className="vw-card-metric-icon" />
                    <div className="vw-card-metric-content">
                      <span className="vw-card-metric-label">MMSI</span>
                      <span className="vw-card-metric-value">{entry.mmsi || '--'}</span>
                    </div>
                  </div>
                  <div className="vw-card-metric">
                    <FontAwesomeIcon icon={faTag} className="vw-card-metric-icon" />
                    <div className="vw-card-metric-content">
                      <span className="vw-card-metric-label">Type</span>
                      <span className="vw-card-metric-value">{entry.ship_type || 'Unknown'}</span>
                    </div>
                  </div>
                  <div className="vw-card-metric">
                    <FontAwesomeIcon icon={faAnchor} className="vw-card-metric-icon" style={{ color: '#28a745' }} />
                    <div className="vw-card-metric-content">
                      <span className="vw-card-metric-label">Load</span>
                      <span className="vw-card-metric-value vw-card-metric-value-text">{portName(entry.loading_port_location) || '—'}</span>
                    </div>
                  </div>
                  <div className="vw-card-metric">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="vw-card-metric-icon" style={{ color: '#dc2626' }} />
                    <div className="vw-card-metric-content">
                      <span className="vw-card-metric-label">Disch</span>
                      <span className="vw-card-metric-value vw-card-metric-value-text">{portName(entry.discharging_port_location) || '—'}</span>
                    </div>
                  </div>
                </div>

                {editingId === entry.id && (
                  <div
                    className="vw-card-ports-editor"
                    ref={editingId === entry.id ? editorRef : null}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <div className="vw-port-field">
                      <label className="vw-port-label">
                        <FontAwesomeIcon icon={faAnchor} style={{ color: '#28a745' }} /> Load port
                      </label>
                      <PortSearchAutocomplete
                        value={entry.loading_port_location || ''}
                        onSelect={(port) => handlePortSelect(entry, 'loading_port_location', port)}
                        placeholder="Search load port…"
                      />
                      {entry.loading_port_location && (
                        <button
                          type="button"
                          className="vw-port-clear"
                          onClick={() => savePort(entry, 'loading_port_location', '')}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="vw-port-field">
                      <label className="vw-port-label">
                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#dc2626' }} /> Discharge port
                      </label>
                      <PortSearchAutocomplete
                        value={entry.discharging_port_location || ''}
                        onSelect={(port) => handlePortSelect(entry, 'discharging_port_location', port)}
                        placeholder="Search discharge port…"
                      />
                      {entry.discharging_port_location && (
                        <button
                          type="button"
                          className="vw-port-clear"
                          onClick={() => savePort(entry, 'discharging_port_location', '')}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {savingId === entry.id && (
                      <span className="vw-search-bar-status">
                        <FontAwesomeIcon icon={faSpinner} spin /> Saving…
                      </span>
                    )}
                  </div>
                )}

                <div className="vw-card-footer">
                  <button
                    type="button"
                    className="vw-card-edit-btn"
                    onClick={(e) => { e.stopPropagation(); setEditingId(prev => (prev === entry.id ? null : entry.id)); }}
                    title="Set load & discharge ports to plot this vessel's route"
                  >
                    <FontAwesomeIcon icon={faRoute} /> {editingId === entry.id ? 'Close ports' : 'Ports'}
                  </button>
                  <button
                    type="button"
                    className="vw-card-remove-btn"
                    onClick={(e) => { e.stopPropagation(); handleRemove(entry); }}
                    disabled={removingId === entry.id}
                    title="Remove from watchlist"
                  >
                    {removingId === entry.id
                      ? <FontAwesomeIcon icon={faSpinner} spin />
                      : <><FontAwesomeIcon icon={faTimes} /> Remove</>
                    }
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
      {editingId != null && <div className="vw-editor-scroll-spacer" aria-hidden="true" />}
    </>
  );
};

export default VesselWatchlistPanel;
